import { paths } from "@src/packages/openapi/schema";
import { RequestBody, ResponseBody } from "@src/packages/openapi/helpers";

export type Patient = ResponseBody<
  paths["/{tenantId}/patients/{patientId}"]["get"]
>["data"];

export type PatchPatient = RequestBody<
  paths["/{tenantId}/patients/{patientId}"]["patch"]
>;

export type PatchMedicalHistory = NonNullable<PatchPatient["medicalHistory"]>;
export type PatchMedicalHistoryMedications = NonNullable<
  PatchMedicalHistory["medications"]
>;
export type PatchMedicalHistoryVaccinations = NonNullable<
  PatchMedicalHistory["vaccinations"]
>;

/**
 * Calculate Basic Info completion in one pass.
 * Returns completion for fields owned by the Basic Info flow.
 *
 * Total is dynamic because some fields are conditional:
 * - +1 if hasBeenPregnant is true (pregnancy count)
 * - +1 if hasAllergies is true (allergy details)
 *
 * Medication history is summarized here, but completion and editing are owned
 * by the categorized Medications & Vaccines flow.
 */
type FieldStatus = {
  name: string;
  filled: boolean;
  value: unknown;
  na?: boolean;
};

export function calculateBasicInfoCompletion(
  patient: Patient | null | undefined,
): {
  percent: number;
  label: string;
  completed: number;
  total: number;
  breakdown?: {
    filled: FieldStatus[];
    missing: FieldStatus[];
    notApplicable: FieldStatus[];
  };
} {
  // Default: 9 base fields (minimum total)
  if (!patient) return { percent: 0, label: "0/9", completed: 0, total: 9 };

  const fields: boolean[] = [];
  // If your API returns different key casing, add normalization above and swap
  // the reads here to use normalized variables. For now we use raw patient.

  // 1. Date of birth
  fields.push(!!patient.dateOfBirth);
  const statuses: FieldStatus[] = [
    {
      name: "dateOfBirth",
      filled: !!patient.dateOfBirth,
      value: patient.dateOfBirth,
    },
  ];

  // 2. Height
  fields.push(!!patient.bodyData?.height);
  statuses.push({
    name: "height",
    filled: !!patient.bodyData?.height,
    value: patient.bodyData?.height,
  });

  // 3. Weight
  fields.push(!!patient.bodyData?.weight);
  statuses.push({
    name: "weight",
    filled: !!patient.bodyData?.weight,
    value: patient.bodyData?.weight,
  });

  // 4. Quick current-substance check. The detailed Substances section owns
  // frequency, route, quantity, dates, and notes; this checkpoint only needs
  // an explicit lifestyle answer so high-signal context is captured early.
  const hasQuickExposureAnswer =
    patient.lifestyle?.hasAddictions !== undefined &&
    patient.lifestyle?.hasAddictions !== null;
  fields.push(hasQuickExposureAnswer);
  statuses.push({
    name: "quickExposure",
    filled: hasQuickExposureAnswer,
    value: patient.lifestyle?.hasAddictions,
  });

  // 5. Has been pregnant status (AFAB only - biologically not applicable to AMAB)
  if (patient.gender !== "AMAB") {
    const hasPregnancyData =
      patient.obGynHistory?.hasBeenPregnant !== undefined &&
      patient.obGynHistory?.hasBeenPregnant !== null;
    fields.push(hasPregnancyData);
    statuses.push({
      name: "hasBeenPregnant",
      filled: hasPregnancyData,
      value: patient.obGynHistory?.hasBeenPregnant,
    });

    // 5. Pregnancy count (only required if hasBeenPregnant is true)
    if (patient.obGynHistory?.hasBeenPregnant === true) {
      const hasPregnancyCount =
        patient.obGynHistory.pregnancies &&
        patient.obGynHistory.pregnancies.length > 0;
      fields.push(!!hasPregnancyCount);
      statuses.push({
        name: "pregnancyCount",
        filled: !!hasPregnancyCount,
        value: patient.obGynHistory?.pregnancies?.length,
      });
    } else {
      // If not pregnant, this field is automatically "complete"
      fields.push(true);
      statuses.push({
        name: "pregnancyCount",
        filled: true,
        value: null,
        na: true,
      });
    }
  }

  // 6. Endometriosis status (AFAB only)
  // "no" is also a valid answer, so check if value is not null/undefined
  if (patient.gender !== "AMAB") {
    const hasEndometriosisAnswered =
      patient.medicalHistory?.hasEndometriosis !== undefined &&
      patient.medicalHistory?.hasEndometriosis !== null;
    fields.push(hasEndometriosisAnswered);
    statuses.push({
      name: "hasEndometriosis",
      filled: hasEndometriosisAnswered,
      value: patient.medicalHistory?.hasEndometriosis,
    });
  }

  // 7. PCOS or Varicocele (gender-specific)
  if (patient.gender === "AMAB") {
    // "no" is also a valid answer, so check if value is not null/undefined
    const hadVaricocelesAnswered =
      patient.medicalHistory?.hadVaricoceles !== undefined &&
      patient.medicalHistory?.hadVaricoceles !== null;
    fields.push(hadVaricocelesAnswered);
    statuses.push({
      name: "hadVaricoceles",
      filled: hadVaricocelesAnswered,
      value: patient.medicalHistory?.hadVaricoceles,
    });
  } else {
    // PCOS can be:
    // 1. Diagnosed (stored in diseases table with disease: "pcos")
    // 2. Suspected/undiagnosed (stored in suspectPCOS: true)
    // 3. No (stored in suspectPCOS: false)
    const hasPCOSDisease =
      patient.medicalHistory?.diseases?.some((d) => d.disease === "pcos") ??
      false;
    const hasSuspectPCOS =
      patient.medicalHistory?.suspectPCOS !== undefined &&
      patient.medicalHistory?.suspectPCOS !== null;
    const pcosAnswered = hasPCOSDisease || hasSuspectPCOS;

    fields.push(pcosAnswered);
    statuses.push({
      name: "suspectPCOS",
      filled: pcosAnswered,
      value: hasPCOSDisease
        ? "diagnosed"
        : patient.medicalHistory?.suspectPCOS === true
          ? "suspected"
          : patient.medicalHistory?.suspectPCOS === false
            ? "no"
            : null,
    });
  }

  // 8. Cancer history
  fields.push(
    patient.medicalHistory?.hasCancerHistory !== undefined &&
      patient.medicalHistory?.hasCancerHistory !== null,
  );
  statuses.push({
    name: "hasCancerHistory",
    filled:
      patient.medicalHistory?.hasCancerHistory !== undefined &&
      patient.medicalHistory?.hasCancerHistory !== null,
    value: patient.medicalHistory?.hasCancerHistory,
  });

  // 9. Allergies status
  const hasAllergiesStatus =
    patient.medicalHistory?.hasAllergies !== undefined &&
    patient.medicalHistory?.hasAllergies !== null;
  fields.push(hasAllergiesStatus);
  statuses.push({
    name: "hasAllergies",
    filled: hasAllergiesStatus,
    value: patient.medicalHistory?.hasAllergies,
  });

  // 10. Allergy details (only required if hasAllergies is true)
  if (patient.medicalHistory?.hasAllergies === true) {
    const hasAllergyDetails =
      patient.medicalHistory.allergies &&
      patient.medicalHistory.allergies.length > 0;
    fields.push(!!hasAllergyDetails);
    statuses.push({
      name: "allergyDetails",
      filled: !!hasAllergyDetails,
      value: patient.medicalHistory?.allergies,
    });
  } else {
    // If no allergies, this field is automatically "complete"
    fields.push(true);
    statuses.push({
      name: "allergyDetails",
      filled: true,
      value: null,
      na: true,
    });
  }

  // Calculate percentage based on required fields ONLY (exclude n/a)
  const requiredStatuses = statuses.filter((s) => !s.na);
  const completedFields = requiredStatuses.filter((s) => s.filled).length;
  const totalFields = requiredStatuses.length;

  const percent = totalFields > 0 ? completedFields / totalFields : 0;

  const filledList = statuses.filter((s) => s.filled && !s.na);
  const missingList = statuses.filter((s) => !s.filled && !s.na);
  const notApplicableList = statuses.filter((s) => s.na);

  return {
    percent,
    label: `${completedFields}/${totalFields}`,
    completed: completedFields,
    total: totalFields,
    breakdown: {
      filled: filledList,
      missing: missingList,
      notApplicable: notApplicableList,
    },
  };
}
